import { ChangeEvent, useState } from 'react';
import { Column } from '@tanstack/react-table';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { TApplication } from 'lendingApi/applications.types';
import { TFilterItem, TItemGroup, TItemValue } from 'types';
import {
  APPLICATION_STATUS_FILTERS,
  TERM_LENGTH_FILTERS,
} from 'utils/applications/applications.config';
import { COLUMN_IDS } from '../../Context/ApplicationsTableState.helpers';
import { useTrackFilter } from '../useTrackFilter.hook';

type TProps = {
  column: Column<TApplication, unknown>;
  onClose: () => void;
};

export const useSelectColumnFilter = ({ column, onClose }: TProps) => {
  const { splitTests } = useIntroducerData();
  const { trackFilterApplied, trackFilterReset } = useTrackFilter(
    column.columnDef.header,
  );
  const initialFilterValue =
    (column.getFilterValue() as string[] | undefined) ?? [];

  const [selectedValues, setSelectedValues] = useState(initialFilterValue);

  const handleChange = ({
    target: { name },
  }: ChangeEvent<HTMLInputElement>) => {
    setSelectedValues((prev) =>
      prev.includes(name)
        ? prev.filter((val) => val !== name)
        : [...prev, name],
    );
  };
  const handleApply = () => {
    column.setFilterValue(selectedValues.length ? selectedValues : undefined);
    trackFilterApplied(selectedValues);
    onClose();
  };

  const handleReset = () => {
    setSelectedValues([]);
    column.setFilterValue(undefined);
    trackFilterReset();
  };

  const options =
    column.columnDef.id === COLUMN_IDS.status
      ? APPLICATION_STATUS_FILTERS()
      : TERM_LENGTH_FILTERS(splitTests?.access_to_prime_loans || false);

  return {
    groupedItems: getGroupedItems(options),
    selectedValues,
    handleChange,
    handleApply,
    handleReset,
  };
};

const getGroupedItems = (items: TFilterItem[]): TItemGroup[] => {
  const groupedItems: TItemGroup[] = [];
  const ungrouped: TItemValue[] = [];

  items.forEach((item) => {
    if ('value' in item) {
      ungrouped.push(item);
    } else if ('items' in item) {
      groupedItems.push(item);
    }
  });

  if (ungrouped.length > 0) {
    groupedItems.push({ group: 'ungrouped', items: ungrouped });
  }

  return groupedItems;
};
